<template>
  <div id="editModalCobranca">
    <q-dialog v-model="dialogAberto" persistent>
      <q-card style="max-width: 600px; width: 100%;">
        <q-card-section class="flex justify-between" >
          <div class="text-h6">Editar Forma de Cobrança</div>

          <q-btn flat dense round v-close-popup icon="close" @click="closeModal" />
        </q-card-section>

        <q-card-section>
          <div class="row q-col-gutter-md">
            <div v-if="isGatewayConexao" class="col-12" data-test="conta-forma-cobranca">
              <q-select
                v-model="gateway.conta_id"
                :options="contas"
                option-value="id"
                option-label="titulo"
                label="Conta"
                emit-value
                map-options
              />
            </div>

            <div
              class="col-md-6 col-12"
              v-for="(opt, nome) in gateway.optionsParams"
              :key="nome"
              :data-test="`opt-${nome}`"
            >
              <q-input
                v-if="opt.type === 'text'"
                :label="labelDaOpcao(nome, opt)"
                v-model="opt.valor"
              />

              <q-input
                v-else-if="opt.type === 'password'"
                :label="labelDaOpcao(nome, opt)"
                v-model="opt.valor"
                type="password"
                autocomplete="new-password"
                :hint="opt.configurado ? 'Configurado. Deixe em branco para manter.' : 'Não configurado'"
              />

              <q-select
                v-else-if="opt.type === 'select'"
                v-model="opt.valor"
                :label="labelDaOpcao(nome, opt)"
                :options="optionsDaOpcao(opt)"
                emit-value
                map-options
                bottom-slots
              >
                <template v-slot:no-option>
                  <q-item>
                    <q-item-section class="text-grey">Sem Resultados</q-item-section>
                  </q-item>
                </template>
              </q-select>

              <q-field
                v-else-if="opt.type === 'number'"
                v-model="opt.valor"
                :label="opt.label"
              >
                <template v-slot:control="{ floatingLabel, modelValue, emitValue }">
                  <money-input
                    @input="emitValue"
                    :value="modelValue"
                    v-bind="percentFormat"
                    v-show="floatingLabel"
                  />
                </template>
              </q-field>
            </div>
          </div>
        </q-card-section>

        <q-card-section v-if="isGatewayConexao && isEquipeDourasoft" class="row items-center q-gutter-sm">
          <q-btn
            outline
            color="primary"
            label="Testar conexão"
            data-test="testar-conexao"
            :loading="testando"
            :disable="testando"
            @click="testConnection"
          />
          <div
            v-if="resultadoConexao"
            data-test="resultado-conexao"
            :class="resultadoConexao.success ? 'text-positive' : 'text-negative'"
          >
            {{ resultadoConexao.message }}
          </div>
        </q-card-section>

        <q-card-section class="flex justify-between">
          <div>
            <q-checkbox
              v-model="gateway.padrao"
              label="Padrão"
              color="green"
              :disable="padraoOriginal"
            />

            <q-checkbox
              v-model="gateway.ativo"
              label="Ativo"
              color="green"
            />
          </div>

          <q-btn
            class="bg-positive text-white"
            label="Salvar"
            data-test="salvar-forma-cobranca"
            :loading="saving"
            @click="save"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import axios from "@/axios-auth";
import { mapGetters } from "vuex";
import { Notify } from "quasar";
import MoneyInput from "@/components/Utils/money-input.vue";

const TIPOS_GATEWAY_CONEXAO = [23, 24];
const LABELS_CONEXAO = {
  company_id: "ID da empresa no gateway",
  conta_empresa_id: "Conta da lotérica no gateway"
};
const LABELS_AMBIENTE = { sandbox: "Homologação", producao: "Produção" };
const CAMPOS_CONEXAO = {
  23: ["ambiente", "username", "company_id", "password", "webhook_secret"],
  24: ["ambiente", "client_id", "client_secret", "account_id", "chave_pix"]
};
const FALHA_CONEXAO = "Não foi possível testar a conexão agora. Tente de novo em instantes.";

export default {
  props: ["editModalCobranca", "gateway"],
  emits: ["closeModal", "listarGateways"],
  components: { MoneyInput },
  data() {
    return {
      saving: false,
      testando: false,
      resultadoConexao: null,
      contas: [],
      /* a forma de cobrança padrão só pode ser trocada por outra, nunca desmarcada,
      para a empresa nunca ficar sem padrão */
      padraoOriginal: false,
      percentFormat: {
        decimal: ".",
        thousands: "",
        prefix: "",
        suffix: " %",
        precision: 2,
        masked: false
      },
    };
  },
  created() {
    this.padraoOriginal = !!this.gateway.padrao;
    if (this.isGatewayConexao) this.loadContas();
  },
  computed: {
    ...mapGetters(["isEquipeDourasoft"]),
    isGatewayConexao() {
      return TIPOS_GATEWAY_CONEXAO.includes(Number(this.gateway.tipo));
    },
    // `v-model` direto numa prop e erro de compilacao no Vue 3
    // ("local prop bindings are not writable"). No Vue 2 funcionava com aviso,
    // mas silenciosamente: fechar por ESC ou clique fora mutava a copia local e o
    // pai nunca era avisado, entao o estado dele continuava aberto.
    dialogAberto: {
      get() {
        return this.editModalCobranca;
      },
      set(aberto) {
        if (!aberto) this.$emit("closeModal");
      },
    },
  },
  methods: {
    loadContas() {
      axios.get("/contas").then(res => {
        this.contas = (res && res.data && res.data.contas) || [];
      });
    },
    labelDaOpcao(nome, opt) {
      return (this.isGatewayConexao && LABELS_CONEXAO[nome]) || opt.label;
    },
    optionsDaOpcao(opt) {
      if (!this.isGatewayConexao) return opt.options;

      return (opt.options || []).map(option => ({ ...option, label: LABELS_AMBIENTE[option.value] || option.label }));
    },
    testConnection() {
      if (this.testando) return;
      this.testando = true;
      this.resultadoConexao = null;

      const dados = {};
      CAMPOS_CONEXAO[Number(this.gateway.tipo)].forEach(campo => {
        const opt = this.gateway.optionsParams[campo];
        dados[campo] = opt && opt.valor ? String(opt.valor) : "";
      });

      axios
        .post(`/gateways/testar-conexao/${this.gateway.id}`, dados)
        .then(res => {
          const corpo = res && res.data;
          this.resultadoConexao = corpo && corpo.message
            ? { success: !!corpo.success, message: corpo.message }
            : { success: false, message: FALHA_CONEXAO };
        })
        .catch(() => {
          this.resultadoConexao = { success: false, message: FALHA_CONEXAO };
        })
        .finally(() => {
          this.testando = false;
        });
    },
    closeModal() {
      if (this.saving) return;
      this.$emit("closeModal");
    },
    save() {
      if (this.saving) return;
      this.saving = true;

      this.gateway.gateways_opts.forEach(opt => {
        if (this.gateway.optionsParams[opt.nome] !== undefined) {
          opt.valor = this.gateway.optionsParams[opt.nome].valor;
        }
      });

      /*procura as opções que não possuem correspondência, ou seja,
      que existe no optionsParams mas não existe no gateways_opts**/
      let keys = Object.keys(this.gateway.optionsParams);
      let chavesSemCorrespondencia = keys.filter(chave =>
        !this.gateway.gateways_opts.some(obj => obj.nome === chave)
      );

      this.gateway.new_gateways_opts = [];

      /*para cada gateway_opt sem correspondecia, um novo objeto sera
      enviado como new_gateways_opts**/
      for(let chave of chavesSemCorrespondencia) {
        const isApus = chave.indexOf("apus_") === 0;
        if(this.gateway.optionsParams[chave].valor || isApus) {
          this.gateway.new_gateways_opts.push({
            nome: chave,
            valor: this.gateway.optionsParams[chave].valor || ""
          });
        }
      }

      const data = {
        id: this.gateway.id,
        titulo: this.gateway.titulo,
        conta_id: this.gateway.conta_id,
        padrao: this.gateway.padrao,
        ativo: this.gateway.ativo,
        gateways_opts: this.gateway.gateways_opts,
        new_gateways_opts: this.gateway.new_gateways_opts
      };

      axios
        .post("/gateways/edit/" + this.gateway.id, data)
        .then(res => {
          this.$emit("closeModal");

          if (!res.data.success) {
            Notify.create({
              message: res.data.message,
              color: "negative",
              position: "top-right"
            });
            return;
          }

          Notify.create({
            message: "Forma de pagamento alterada com sucesso.",
            color: "positive",
            position: "top-right"
          });

          this.$emit("listarGateways");
          this.saving = false;
        })
        .catch(() => {
          Notify.create({
            message: "Não foi possível salvar, ou você não possui permissão!",
            color: "red",
            position: "top-right"
          });
        });
    }
  }
};
</script>